import { FolderInput, MessageSquare, Trash2 } from "lucide-react"
import { useState } from "react"

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"

import { chatDragProps } from "@/lib/chat-dnd"
import { cn } from "@/lib/utils"
import type { Conversation, Project } from "@/types/api"

export function ConversationItem({
  conversation,
  active,
  onSelect,
  onDelete,
  projects,
  onMoveToProject,
}: {
  conversation: Conversation
  active: boolean
  onSelect: () => void
  onDelete: () => void
  projects: Project[]
  onMoveToProject: (projectId: string | null) => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  return (
    <div
      role="button"
      tabIndex={0}
      {...chatDragProps(conversation.id)}
      onClick={onSelect}
      onKeyDown={(e) => e.key === "Enter" && onSelect()}
      className={cn(
        "group flex h-[38px] w-full cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-[13.5px] transition-colors",
        active
          ? "border-l-[3px] border-orange bg-arctic pl-2 font-semibold text-[#2b2b2b]"
          : "text-fg2 hover:bg-hover",
      )}
    >
      <MessageSquare className={cn("size-3.5 shrink-0", active ? "text-[#2b2b2b]" : "text-fg3")} />
      <span className="flex-1 truncate" title={conversation.title}>
        {conversation.title}
      </span>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            onClick={(event) => event.stopPropagation()}
            aria-label={`Conversation actions for "${conversation.title}"`}
            title="Conversation actions"
            className={cn("shrink-0 rounded p-0.5 opacity-0 transition-opacity group-hover:opacity-100 hover:text-foreground focus-visible:opacity-100", active ? "text-[#2b2b2b]/50" : "text-fg4", menuOpen && "opacity-100")}
          >
            <FolderInput className="size-3.5" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" onClick={(event) => event.stopPropagation()}>
          {conversation.project_id && <DropdownMenuItem onSelect={() => onMoveToProject(null)}>Move to regular chats</DropdownMenuItem>}
          {projects.filter((project) => project.id !== conversation.project_id).map((project) => (
            <DropdownMenuItem key={project.id} onSelect={() => onMoveToProject(project.id)}>Move to {project.name}</DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDelete() }}
        aria-label={`Delete conversation "${conversation.title}"`}
        title="Delete"
        className={cn("shrink-0 rounded p-0.5 opacity-0 transition-opacity group-hover:opacity-100 hover:text-destructive focus-visible:opacity-100", active ? "text-[#2b2b2b]/50" : "text-fg4")}
      >
        <Trash2 className="size-3.5" />
      </button>
    </div>
  )
}
